import React from 'react';
import { Box, Button, Skeleton, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { apiGetQuickLinks } from 'api';
import Card from 'components/card/Card';

interface QuickLink {
  id: number;
  title: string;
  url: string;
  open_in_new_tab: boolean;
}

export const QuickLinks: React.FC = () => {
  const bgColor = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('#CBD5E0', 'rgba(135,140,189,0.5)');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const { data, isLoading } = useQuery({
    queryKey: ['homepage-quick-links'],
    queryFn: () => apiGetQuickLinks() as Promise<QuickLink[]>,
    staleTime: 10 * 60 * 1000,
  });

  const links: QuickLink[] = data ?? [];

  return (
    <Card
      bg={bgColor}
      borderRadius="16px"
      border="1px solid"
      borderColor={borderColor}
      p="0"
      overflow="hidden"
    >
      <Box px="20px" py="16px" borderBottom="1px solid" borderColor={borderColor}>
        <Text fontSize="sm" fontWeight="bold" color={textColor}>
          Quick Links
        </Text>
      </Box>

      <VStack spacing="8px" p="12px">
        {isLoading ? (
          [1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height="36px" w="100%" borderRadius="8px" />
          ))
        ) : links.length === 0 ? (
          <Text fontSize="xs" color="gray.400" textAlign="center" py="12px">
            No quick links configured
          </Text>
        ) : (
          links.map((link) => (
            <Button
              key={link.id}
              as="a"
              href={link.url}
              target={link.open_in_new_tab ? '_blank' : '_self'}
              rel="noopener noreferrer"
              w="100%"
              variant="outline"
              size="sm"
              borderRadius="8px"
              fontWeight="600"
              fontSize="sm"
              justifyContent="center"
              colorScheme="gray"
              _hover={{ bg: 'brand.50', borderColor: 'brand.300', color: 'brand.600' }}
            >
              {link.title}
            </Button>
          ))
        )}
      </VStack>
    </Card>
  );
};
